/**
 * Carte céleste antique en arrière-plan du panel admin.
 * Server Component statique. L'apparence change via CSS selon `:root.dark`.
 *
 *   Light mode : trace sépia ~6% d'opacité — on devine à peine la structure.
 *   Dark mode  : ciel révélé ~22% d'opacité, dust stars qui scintillent.
 *
 * Le même SVG est rendu dans les 2 cas — c'est le même ciel, on ne le voit
 * que quand il fait noir.
 */
export function CelestialMap() {
  return (
    <div className="mf-celestial" aria-hidden="true">
      <svg
        viewBox="0 0 1600 1000"
        preserveAspectRatio="xMidYMid slice"
        className="mf-celestial__svg"
      >
        {/* Écliptique : courbe sinusoïdale qui sert de support au zodiaque */}
        <path
          d="M 0 500 Q 400 400, 800 500 T 1600 500"
          fill="none"
          className="mf-celestial__ecliptic"
        />

        {/* Bande zodiacale : 12 symboles + nom latin */}
        <g className="mf-celestial__zodiac">
          {ZODIAC.map((z) => (
            <g key={z.symbol}>
              <text
                x={z.x}
                y={z.y}
                className="mf-celestial__zodiac-symbol"
              >
                {z.symbol}
              </text>
              <text
                x={z.x}
                y={z.y + 16}
                className="mf-celestial__zodiac-name"
              >
                {z.name}
              </text>
            </g>
          ))}
        </g>

        {/* Constellations : lignes + anchor stars + label */}
        <g className="mf-celestial__constellations">
          {CONSTELLATIONS.map((c) => (
            <g key={c.name}>
              {c.lines.map(([from, to], j) => (
                <line
                  key={j}
                  x1={c.stars[from][0]}
                  y1={c.stars[from][1]}
                  x2={c.stars[to][0]}
                  y2={c.stars[to][1]}
                  className="mf-celestial__line"
                />
              ))}
              {c.stars.map(([x, y], j) => (
                <circle
                  key={j}
                  cx={x}
                  cy={y}
                  r="2"
                  className="mf-celestial__anchor-star"
                />
              ))}
              <text
                x={c.label[0]}
                y={c.label[1]}
                className="mf-celestial__constellation-name"
              >
                {c.name}
              </text>
            </g>
          ))}
        </g>

        {/* Poussière d'étoiles : ~80 petits points qui scintillent en dark */}
        <g className="mf-celestial__dust">
          {DUST_STARS.map(([x, y, r], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={r}
              style={{ animationDelay: `${(i % 9) * 0.4}s` }}
              className="mf-celestial__dust-star"
            />
          ))}
        </g>
      </svg>
    </div>
  );
}

// Zodiaque : positions calculées le long de la courbe écliptique (cf. path ci-dessus)
const ZODIAC = [
  { symbol: "♈", name: "ARIES",       x: 100,  y: 478 },
  { symbol: "♉", name: "TAURUS",      x: 215,  y: 445 },
  { symbol: "♊", name: "GEMINI",      x: 330,  y: 418 },
  { symbol: "♋", name: "CANCER",      x: 445,  y: 402 },
  { symbol: "♌", name: "LEO",         x: 560,  y: 408 },
  { symbol: "♍", name: "VIRGO",       x: 675,  y: 442 },
  { symbol: "♎", name: "LIBRA",       x: 800,  y: 498 },
  { symbol: "♏", name: "SCORPIUS",    x: 925,  y: 555 },
  { symbol: "♐", name: "SAGITTARIUS", x: 1040, y: 590 },
  { symbol: "♑", name: "CAPRICORNUS", x: 1155, y: 600 },
  { symbol: "♒", name: "AQUARIUS",    x: 1270, y: 585 },
  { symbol: "♓", name: "PISCES",      x: 1385, y: 555 },
];

// Constellations stylisées. stars = [x, y]. lines = pairs d'index.
const CONSTELLATIONS: ReadonlyArray<{
  name: string;
  label: [number, number];
  stars: ReadonlyArray<[number, number]>;
  lines: ReadonlyArray<[number, number]>;
}> = [
  {
    name: "Orion",
    label: [165, 360],
    stars: [
      [180, 130], // Betelgeuse
      [110, 150], // Bellatrix
      [130, 220], // Mintaka
      [170, 230], // Alnilam
      [210, 240], // Alnitak
      [240, 310], // Saiph
      [90,  320], // Rigel
    ],
    lines: [
      [0, 3], [1, 3], [2, 3], [3, 4], [4, 5], [2, 6], [0, 4], [1, 2],
    ],
  },
  {
    name: "Cassiopeia",
    label: [1420, 215],
    stars: [
      [1320, 140],
      [1370, 105],
      [1420, 165],
      [1470, 105],
      [1520, 140],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
  {
    name: "Ursa Major",
    label: [380, 470],
    stars: [
      [300, 380],
      [340, 370],
      [380, 360],
      [420, 380],
      [460, 405],
      [435, 440],
      [395, 432],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 3]],
  },
  {
    name: "Cygnus",
    label: [1190, 320],
    stars: [
      [1180, 150], // Deneb
      [1180, 220], // Sadr (centre de la croix)
      [1180, 280], // Albireo (queue)
      [1110, 215], // aile gauche
      [1250, 220], // aile droite
    ],
    lines: [[0, 1], [1, 2], [3, 1], [1, 4]],
  },
  {
    name: "Lyra",
    label: [1380, 730],
    stars: [
      [1350, 580], // Vega
      [1330, 625],
      [1380, 640],
      [1395, 695],
      [1340, 680],
    ],
    lines: [[0, 1], [0, 2], [1, 2], [2, 3], [3, 4], [4, 1]],
  },
  {
    name: "Aquila",
    label: [240, 800],
    stars: [
      [200, 700],
      [240, 720], // Altair
      [285, 745],
      [240, 775],
    ],
    lines: [[0, 1], [1, 2], [1, 3]],
  },
  {
    name: "Pegasus",
    label: [740, 805],
    stars: [
      [690, 695],
      [780, 695],
      [780, 780],
      [690, 780],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 0]],
  },
  {
    name: "Andromeda",
    label: [890, 690],
    stars: [
      [780, 695],
      [820, 685],
      [860, 670],
      [900, 660],
      [940, 655],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
  {
    name: "Corona Borealis",
    label: [625, 335],
    stars: [
      [550, 290],
      [575, 275],
      [605, 270],
      [635, 274],
      [665, 285],
      [695, 305],
      [718, 330],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6]],
  },
  {
    name: "Perseus",
    label: [475, 175],
    stars: [
      [460, 95],
      [495, 130],
      [510, 175],
      [475, 215],
      [430, 195],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
];

// Dust stars : ~80 points dispersés sur le viewport. [x, y, r]
const DUST_STARS: ReadonlyArray<[number, number, number]> = [
  [40, 50, 0.8], [85, 90, 1.1], [150, 40, 0.7], [240, 80, 1.0],
  [310, 50, 0.9], [395, 95, 1.2], [475, 60, 0.8], [555, 30, 1.0],
  [625, 75, 0.7], [690, 110, 1.1], [765, 45, 0.9], [825, 90, 1.0],
  [905, 55, 0.8], [980, 110, 1.1], [1055, 70, 0.9], [1140, 35, 1.2],
  [1215, 95, 0.8], [1290, 65, 1.0], [1390, 50, 0.9], [1455, 105, 0.7],
  [1540, 80, 1.1], [1580, 175, 0.9],

  [60, 175, 1.0], [220, 200, 0.7], [365, 165, 1.0], [420, 250, 0.8],
  [560, 195, 0.9], [615, 230, 0.7], [785, 220, 1.0], [855, 175, 0.8],
  [965, 240, 0.9], [1075, 195, 0.7], [1200, 240, 1.0], [1335, 200, 0.8],
  [1505, 250, 0.9],

  [55, 280, 0.8], [165, 330, 0.9], [310, 290, 0.7], [505, 320, 1.1],
  [710, 295, 0.7], [885, 320, 0.9], [995, 290, 1.0], [1135, 330, 0.8],
  [1275, 290, 0.7], [1430, 335, 1.0], [1535, 290, 0.7],

  [110, 480, 0.8], [260, 510, 0.7], [385, 495, 0.9], [505, 480, 0.7],
  [905, 470, 0.8], [1020, 495, 0.9], [1175, 510, 0.7], [1320, 485, 0.9],
  [1490, 480, 0.8],

  [60, 615, 1.0], [180, 650, 0.7], [310, 625, 0.9], [445, 660, 0.8],
  [565, 620, 0.7], [665, 645, 1.0], [820, 615, 0.7], [945, 660, 1.0],
  [1095, 625, 0.8], [1230, 655, 0.9], [1410, 625, 0.7], [1525, 660, 1.1],

  [50, 790, 0.8], [165, 830, 0.7], [320, 805, 1.0], [410, 855, 0.7],
  [555, 815, 0.8], [625, 865, 0.7], [810, 835, 0.9], [925, 805, 0.7],
  [1085, 865, 1.0], [1195, 815, 0.7], [1345, 855, 0.9], [1495, 825, 0.8],

  [80, 920, 0.7], [220, 940, 0.9], [365, 925, 0.7], [495, 955, 0.8],
  [625, 920, 0.7], [770, 945, 0.9], [935, 920, 0.7], [1080, 950, 0.8],
  [1245, 925, 0.7], [1395, 945, 0.9], [1530, 920, 0.7],
];
